import { collection, getDocsFromServer, limit, query, where, type Firestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { addRxPlugin, createRxDatabase, type MangoQuerySelector, type RxCollection, type RxDocument } from 'rxdb';
import { BehaviorSubject, filter, bufferTime, type Observable } from 'rxjs';

import { RxDBUpdatePlugin } from 'rxdb/plugins/update';
import { RxDBDevModePlugin, disableWarnings } from 'rxdb/plugins/dev-mode';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { RxDBStatePlugin } from 'rxdb/plugins/state';
import { cleanupRxCollection, DEFAULT_CLEANUP_POLICY, RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { replicateFirestore, SyncOptionsFirestore, type RxFirestoreReplicationState } from 'rxdb/plugins/replication-firestore';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { setPremiumFlag } from 'rxdb-premium/plugins/shared';
import { getRxStorageWorker } from 'rxdb-premium/plugins/storage-worker';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';

import { searchService, archivedSearchService, type SearchService } from '@/services/search-service';
import { SEARCH_INDEX_DB_NAME } from '@/services/search-service/indexeddb-utils';
import type { RawSearchDoc } from '@/services/search-service/types';
import { capture } from '@/analytics';
import { clearPreviewUrlCache } from '@/services/firebase-storage-service';
import { tagNameCache } from './tag-name-cache';
import { documentCollections } from './constants';
import {
  SYNC_STATE_KEY,
  markInitialSyncComplete,
  getInitialSyncComplete$,
  isInitialSyncComplete,
  type SyncState,
} from './sync-status';
import SchemasAndMigrations from './rxdb-schemas';
import { getPostRemove, getPreSave, getPostSave, getPreInsert } from './rxdb-middleware';
import { TAGGED_COLLECTIONS, recordPushConflictsForTagCounts, subscribeTagCountsToLocalWrites } from './tag-handler';
import { withServerPreRead } from './replication-push-pre-read';
import { countUnpushedWrites, countUnpushedWritesWithoutReplication, fingerprintServerState } from './unpushed-writes';
import { withTimeout } from '@/utils/promise-utils';
import { tabSyncService } from '@/services/tab-sync-service';
import { ENVIRONMENT } from '@/constants';
import {
  buildTombstoneShell,
  isArchived,
  isArchivePartitioned,
  isDeleted,
  notArchivedSelector,
  type RxCollectionName,
  type RxDatabaseCollections,
  type RxDocumintDatabase,
  type User,
} from '@flintmint/core-shared/rxdb';
import { deferred } from '@flintmint/core-shared/utils';

const INDEXED_DB_WORKER_PATH = '/workers/indexeddb.worker.js';
// A forced sign-out logs every open tab out at once, so the leader's delete can stay blocked
// until the sibling tabs finish closing their connections and redirect.
const INDEXED_DB_DELETE_BLOCKED_TIMEOUT_MS = 5_000;
// How long a non-leader tab waits to inherit leadership during logout before leaving the
// deletion to the tab that already holds it.
const DELETION_LEADERSHIP_TIMEOUT_MS = 2_000;
// Bound on each logout teardown step (cancelling replications, closing the database). Every storage
// call runs over the worker's request/reply transport, which awaits each reply with no timeout, so
// one unanswered call would otherwise pin logout forever.
const TEARDOWN_STEP_TIMEOUT_MS = 5_000;
// How often awaitInSync re-reads the unpushed-write count while the logout modal waits.
const UNPUSHED_WRITES_RECHECK_MS = 1_000;
// How long a non-leader tab waits for the leader to answer its unpushed-write count request.
const LEADER_COUNT_TIMEOUT_MS = 3_000;
// Set when a logout could not delete the local databases; the next start deletes them before
// opening anything (see deleteDatabasesLeftByLogout). Written after logout's localStorage.clear().
export const PENDING_DATABASE_DELETION_KEY = 'documint:pendingLocalDatabaseDeletion';
// Per archive-partitioned collection: what its archive partition pulled (see archivePulledFingerprints),
// kept for the tab that inherits leadership. Cleared by logout's localStorage.clear().
const ARCHIVE_PULLED_FINGERPRINTS_KEY_PREFIX = 'documint:archivePulledFingerprints:';
// Bounded so the list cannot starve other localStorage users; ~105 characters per document.
const MAX_STORED_FINGERPRINTS_LENGTH = 250_000;
// Coalesces a downloaded batch's fingerprints into one save.
const FINGERPRINT_SAVE_DELAY_MS = 100;
// How often the next start retries that deletion before refusing to open the database. Each
// blocked attempt already waits INDEXED_DB_DELETE_BLOCKED_TIMEOUT_MS, so this is roughly half a minute.
const PENDING_DELETION_ATTEMPTS = 5;
const PENDING_DELETION_RETRY_MS = 1_000;
// Buffer per-doc change events into arrays so the search worker receives one
// message per batch instead of one per doc (RxDB fans a 100-doc bulkWrite into
// 100 insert$ events during sync). Small enough to keep the doculist streaming.
const SEARCH_HOOK_BUFFER_MS = 200;
// Margin on top of the buffer window when waiting for buffered search-hook
// batches to flush before persisting the index (see startReplication).
const SEARCH_HOOK_FLUSH_MARGIN_MS = 50;
// Chunk size when rebuilding the whole index from RxDB, awaited per batch for
// backpressure and main-thread yielding between chunks.
const REBUILD_INDEX_BATCH_SIZE = 200;

setPremiumFlag();
disableWarnings();

if (ENVIRONMENT.MODE === 'development') {
  addRxPlugin(RxDBDevModePlugin);
}
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBUpdatePlugin);
addRxPlugin(RxDBStatePlugin);
addRxPlugin(RxDBLeaderElectionPlugin); // needed for multiInstance to work correctly
addRxPlugin(RxDBCleanupPlugin);

export const RXDB_NAME = 'documint';

const REPLICATION_IDENTIFIER = `https://firestore.googleapis.com/${ENVIRONMENT.VITE_FIREBASE_PROJECT_ID}`;

export class LocalDataInitCancelledError extends Error {
  constructor() {
    super('Local data initialization was cancelled');
    this.name = 'LocalDataInitCancelledError';
  }
}

// A previous logout's local databases are still on disk and could not be deleted, typically
// because another open tab still holds them. The database is not opened until they are gone.
export class LocalDataLeftByLogoutError extends Error {
  constructor() {
    super('The local data of a previous logout could not be deleted');
    this.name = 'LocalDataLeftByLogoutError';
  }
}

type ArchivePulledFingerprints = {
  // Document id → fingerprint of the server state pulled.
  fingerprints: Map<string, string>;
  // The coalesced save's timer, while one is pending.
  pendingSave: ReturnType<typeof setTimeout> | undefined;
  // The list grew too large, or the browser rejected it: it lives in memory only.
  savesStopped: boolean;
  // What earlier leaders stored has been merged into fingerprints.
  storedMerged: boolean;
};

class LocalDataService {
  private rxCollections: RxDatabaseCollections | undefined;
  private rxDB: RxDocumintDatabase | undefined;
  private rxDBInitPromise: Promise<RxDocumintDatabase> | undefined;
  private replicationInitPromise: Promise<void> | undefined;
  private replicationStates: Map<string, RxFirestoreReplicationState<any>> = new Map();
  // Archived partitions are tracked separately: they start post-app-ready (possibly while the
  // active initial sync is still awaiting) and must never be counted by the initial-sync latch
  // or the delta capture, which iterate replicationStates.
  private archivedReplicationStates: Map<string, RxFirestoreReplicationState<any>> = new Map();
  // Per collection: what its archive partition pulled this session, plus what earlier leaders stored
  // once merged in (see mergeStoredFingerprints). Also saved to localStorage (see
  // saveArchivePulledFingerprints). Cleared on logout in clearRxDB.
  private archivePulledFingerprints: Map<string, ArchivePulledFingerprints> = new Map();
  private archivedReplicationInitPromise: Promise<void> | undefined;
  private syncInProgress = false;
  private onlineHandler: (() => void) | null = null;
  private lifecycleVersion = 0;
  private syncState: SyncState | undefined;
  private syncStatusWriteQueue: Promise<void> = Promise.resolve();
  // Startup-telemetry baselines threaded in from LocalDataProvider: the private-area entry
  // (provider mount, a perf.now timestamp) and the already-computed time-to-usable delta. Kept
  // here so sync events can measure from entry and copy time_to_usable_ms without the service
  // importing the provider.
  private appEntryAtMs: number | undefined;
  private usableMs: number | undefined;
  // Settled on clearRxDB so a detached delta capture that never reached in-sync (offline start)
  // stops awaiting and releases its states snapshot instead of pinning it past teardown.
  private lifecycleTeardown = deferred<void>();
  // In-flight rebuild promise, shared so overlapping rebuild triggers join one bracket (see
  // rebuildSearchIndex). Cleared in the finally once the rebuild settles.
  private rebuildInProgress: Promise<void> | undefined;
  // First-archive-open latch: ensureArchivedSearchIndex runs the full start sequence
  // once. Kept on success; cleared on failure so the next useArchivedSearch mount
  // retries the reconcile. Hooks subscribe once regardless (the boolean below) so a
  // retry can never double-subscribe. All reset in clearRxDB.
  private archivedSearchInitPromise: Promise<void> | undefined;
  private archivedSearchHooksInitialized = false;
  // While the reconcile bracket is open, live hook sends queue here (FIFO) instead of
  // posting: live events and snapshot chunks would otherwise interleave in the worker
  // queue — a stale snapshot chunk would re-add a doc restored mid-reconcile, and
  // removeUnseenDocsFromIndex would evict a doc archived mid-reconcile (its id is
  // absent from the snapshot). Flushed synchronously after reconcileFinished; always
  // drained in the driver's finally so a failed reconcile can't strand events.
  private archivedReconcileInProgress = false;
  private pendingArchivedSends: Array<() => Promise<void>> = [];
  // "Account is known/assumed to have no documents" — display-only signal mirrored into
  // knownEmptyAccountAtom by LocalDataProvider. Written by the empty-account probe
  // (probeEmptyAccount); reset to false in clearRxDB.
  private emptyAccount$ = new BehaviorSubject<boolean>(false);
  // Tag-count writes issued from the change-stream subscription but not yet committed. They are
  // not awaited in the stream, so getUnpushedWriteCount/syncAndClearFlag drain this set first — otherwise
  // logout could delete the local database between a document commit and its tag write (see
  // docs/tag-counts/tag-counts-architecture.md). Cleared on logout in clearRxDB.
  private pendingTagCountWrites: Set<Promise<void>> = new Set();
  // The storage workers each database opened, terminated on teardown (see getStorage).
  private storageWorkers = new WeakMap<RxDocumintDatabase, Worker[]>();

  constructor() {
    // The leader answers the unpushed-write count for the other tabs (see getUnpushedWriteCount);
    // every other tab stays silent. Lives as long as the page, like the channel itself.
    tabSyncService.answerUnpushedWriteCountRequests(async () =>
      this.rxDB?.isLeader() ? this.countUnpushedWritesInThisTab() : null
    );

    // A tab that follows another tab's logout deletes nothing (see Logout), but the logging-out leader
    // releases its leadership when it closes its database. Stop this tab's replications first, or they
    // win that leadership and start against a database that is being deleted. Cancelling one still
    // waiting for leadership marks it stopped, and RxDB checks that before it starts.
    tabSyncService.subscribeToLogout(() => {
      [...this.replicationStates.values(), ...this.archivedReplicationStates.values()].forEach((state) =>
        state.cancel().catch((error) => console.warn('[CLEANUP] Cancelling a replication for a followed logout failed:', error))
      );
    });

    // A pending fingerprint save's timer does not run once the page goes away (a reload during
    // sync), so save it now. Both live as long as the page.
    window.addEventListener('pagehide', () => this.saveScheduledArchivePulledFingerprints());
    // pagehide is not delivered when a backgrounded tab is discarded, so hiding the page saves too.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.saveScheduledArchivePulledFingerprints();
      }
    });

    // The archived index heals through its reconcile, and nothing else re-runs it: the
    // useArchivedSearch effect never re-fires on status. Drop the latch so the start sequence
    // can run again, and run it here rather than waiting for the next archive open.
    // LocalDataProvider owns the equivalent subscription for the main index.
    archivedSearchService.indexStatus.subscribe((status) => {
      if (status !== 'restore-failed') {
        return;
      }

      this.archivedSearchInitPromise = undefined;
      // Nothing was fed to the worker yet; the first archive open runs the sequence anyway.
      if (!this.rxCollections) {
        return;
      }

      this.ensureArchivedSearchIndex().catch((error) => {
        console.error('[LOCAL_DATA] Archived search index reconcile after failed restore failed:', error);
      });
    });
  }

  recordAppEntry(entryAtMs: number): void {
    this.appEntryAtMs = entryAtMs;
  }

  recordUsableAt(usableMs: number): void {
    this.usableMs = usableMs;
  }

  async initRxDB(): Promise<RxDocumintDatabase> {
    if (this.rxDB && this.rxCollections) {
      return this.rxDB;
    }

    if (this.rxDBInitPromise) {
      return this.rxDBInitPromise;
    }

    const rxDBInitPromise = this.initializeRxDB();
    this.rxDBInitPromise = rxDBInitPromise;

    try {
      return await rxDBInitPromise;
    } finally {
      if (this.rxDBInitPromise === rxDBInitPromise) {
        this.rxDBInitPromise = undefined;
      }
    }
  }

  private async initializeRxDB(): Promise<RxDocumintDatabase> {
    const initLifecycleVersion = ++this.lifecycleVersion;
    let rxDB: RxDocumintDatabase | undefined;
    let rxCollections: RxDatabaseCollections | undefined;

    try {
      await this.deleteDatabasesLeftByLogout();
      rxDB = await this.createRxDB();
      const initializedRxCollections = await rxDB.addCollections(SchemasAndMigrations);
      rxCollections = initializedRxCollections;

      this.throwIfLifecycleChanged(initLifecycleVersion);

      await tagNameCache.init(initializedRxCollections.tags);
      this.throwIfLifecycleChanged(initLifecycleVersion);

      const rxCollectionsArr = Object.values(initializedRxCollections);

      rxCollectionsArr.forEach((rxCollection) => {
        if (isSearchIndexed(rxCollection)) {
          this.initSearchHooks(rxCollection);
        }
        this.initMiddlewareHooks(rxCollection, initializedRxCollections);
        this.initTagCountSubscription(rxCollection, initializedRxCollections);
      });

      await this.initSyncState(rxDB);
      this.throwIfLifecycleChanged(initLifecycleVersion);

      this.rxDB = rxDB;
      this.rxCollections = initializedRxCollections;

      return rxDB;
    } catch (error) {
      if (rxCollections) {
        tagNameCache.reset(rxCollections.tags);
      }
      // Removed only when a logout cancelled this init. A genuine init failure (DB6/COL12) is
      // recovered through logout, which asks before discarding writes that were never pushed.
      await this.cleanupRxDBInstance(rxDB, { remove: !this.isLifecycleCurrent(initLifecycleVersion) });

      if (this.isLifecycleCurrent(initLifecycleVersion)) {
        this.rxDB = undefined;
        this.rxCollections = undefined;
      }

      const lifecycleChanged = !this.isLifecycleCurrent(initLifecycleVersion);
      const initWasCancelled = error instanceof LocalDataInitCancelledError;
      if (lifecycleChanged || initWasCancelled) {
        throw new LocalDataInitCancelledError();
      }

      throw error;
    }
  }

  async initReplication(firestore: Firestore, userId: string): Promise<void> {
    if (!this.rxDB) {
      throw new Error('rxDB must be initialized before starting replication');
    }

    if (this.replicationInitPromise) {
      return this.replicationInitPromise;
    }

    const replicationInitPromise = this.startReplication(firestore, userId);
    this.replicationInitPromise = replicationInitPromise;

    try {
      return await replicationInitPromise;
    } catch (error) {
      if (this.replicationInitPromise === replicationInitPromise) {
        this.replicationInitPromise = undefined;
      }
      throw error;
    }
  }

  async initArchivedReplication(firestore: Firestore, userId: string): Promise<void> {
    if (!this.rxDB) {
      throw new Error('rxDB must be initialized before starting archived replication');
    }

    if (this.archivedReplicationInitPromise) {
      return this.archivedReplicationInitPromise;
    }

    const archivedReplicationInitPromise = this.startArchivedReplication(firestore, userId);
    this.archivedReplicationInitPromise = archivedReplicationInitPromise;

    try {
      return await archivedReplicationInitPromise;
    } catch (error) {
      if (this.archivedReplicationInitPromise === archivedReplicationInitPromise) {
        this.archivedReplicationInitPromise = undefined;
      }
      throw error;
    }
  }

  private async startArchivedReplication(firestore: Firestore, userId: string): Promise<void> {
    this.archivePartitionedCollections.forEach((rxCollection) =>
      this.replicateArchivedCollection(rxCollection, firestore, userId)
    );
  }

  private get archivePartitionedCollections(): RxCollection[] {
    return Object.values(this.rxCollections!).filter((rxCollection) => isArchivePartitioned(rxCollection));
  }

  private async startReplication(firestore: Firestore, userId: string): Promise<void> {
    // Capture the latch before replication so a warm reload (latch already true)
    // is never counted as an initial sync. Only the leader tab reaches the code
    // below, since awaitInitialReplication resolves for the leader alone.
    const isFirstSyncOnDevice = !isInitialSyncComplete(this.syncState!);
    const lifecycleAtLaunch = this.lifecycleVersion;
    const startedAtMs = performance.now();
    const isFirstStart = this.replicationStates.size === 0;

    if (isFirstStart) {
      if (isFirstSyncOnDevice) {
        void this.probeEmptyAccount(firestore, userId);
      }

      const rxCollectionsArr = Object.values(this.rxCollections!);
      rxCollectionsArr.forEach((collection) => this.replicateCollection(collection, firestore, userId));

      this.setupNetworkDetection();

      // Warm reload (latch already set): measure the delta catch-up separately, detached so a
      // never-in-sync (offline) start simply never emits and never blocks replication startup.
      if (!isFirstSyncOnDevice) {
        void this.captureDeltaSyncCompleted();
      }
    }

    await Promise.all(Array.from(this.replicationStates.values()).map((state) => state.awaitInitialReplication()));
    // The final change events of the initial sync can still be sitting in an
    // unflushed bufferTime window when awaitInitialReplication resolves. Wait one
    // buffer interval plus a margin so every buffered batch is posted to the
    // worker BEFORE the persist message, preserving the "persisted index ⇒
    // complete index" invariant (a partial persisted index would permanently skip
    // the rebuild path on the next launch).
    await new Promise((resolve) => setTimeout(resolve, SEARCH_HOOK_BUFFER_MS + SEARCH_HOOK_FLUSH_MARGIN_MS));
    // Only persist when the index isn't mid-rebuild: a stale-path rebuild sets status 'rebuilding'
    // synchronously on the main thread, whereas its rebuildStarted reaches the worker async. If this
    // persist were enqueued first it would durably store the near-empty pre-rebuild index under the
    // new active index key (and stick if the rebuild later fails). Still send the message so the worker's
    // persist gate opens for later batch persists; rebuildFinished persists the whole index.
    try {
      await searchService.replicationCompleted({ persistIndex: searchService.indexStatus.value === 'ready' });
    } catch (error) {
      // A failed persist must not cost the latch below — it is the only writer of
      // initial-sync-complete, so losing it strands this session in a loading state and
      // re-runs full initial replication on every later launch. The index itself is safe:
      // the worker already deleted its key, so the next boot rebuilds.
      // Logout tore the session down mid-persist: not a failure, and syncState is gone.
      if (this.lifecycleVersion !== lifecycleAtLaunch) return;
      console.error('[LOCAL_DATA] Failed to persist search index after initial sync:', error);
    }
    await markInitialSyncComplete(this.syncState!);

    if (isFirstSyncOnDevice && isFirstStart) {
      await this.captureInitialSyncCompleted(startedAtMs);
    }
  }

  // Empty-account fast path: only on the first sync on this device, fire a detached
  // Firestore existence probe over the collections backing document surfaces (notes
  // incl. the templates variant, userSources, blocks) so empty accounts can leave
  // loading UI before the full initial-sync latch; tag/instruction queries keep the
  // normal sync gate (see useSearch). Tombstones (_deleted docs) count as empty:
  // permanently deleted docs are invisible to the client UI, so an account holding
  // only tombstones looks exactly like a fresh one — while initial sync still pulls
  // every tombstone shell, which is precisely when this fast path matters. Every
  // replicated doc carries an explicit _deleted boolean in Firestore, so the
  // equality clause never misses a live doc. Never blocks or
  // delays replication; errors are swallowed. Server-only reads: cache-tolerant
  // getDocs resolves with an empty fromCache snapshot while offline, which would
  // falsely latch known-empty.
  private async probeEmptyAccount(firestore: Firestore, userId: string): Promise<void> {
    const lifecycleAtLaunch = this.lifecycleVersion;
    let allEmpty: boolean;
    try {
      const snapshots = await Promise.all(
        documentCollections.map((collectionName) => {
          const rxCollection = this.rxCollections![collectionName];
          const probeQuery = query(
            collection(firestore, rxCollection.name),
            ...pullFilterFor(rxCollection, userId),
            where('_deleted', '==', false),
            limit(1)
          );
          return getDocsFromServer(probeQuery);
        })
      );
      allEmpty = snapshots.every((snapshot) => snapshot.empty);
    } catch {
      // Probe failure just means the fast path doesn't fire: the account falls back
      // to the baseline initial-sync loading behavior.
      return;
    }
    // A probe resolving after logout/teardown (clearRxDB bumped the version and
    // reset the subject) must not write against the reset baseline.
    if (this.lifecycleVersion !== lifecycleAtLaunch) return;
    if (allEmpty) {
      this.emptyAccount$.next(true);
    }
  }

  private async captureInitialSyncCompleted(startedAtMs: number): Promise<void> {
    try {
      capture('initial_sync_completed', {
        duration_ms: Math.round(performance.now() - startedAtMs),
        time_to_synced_ms: this.timeSinceEntry(),
        ...this.usableCopy(),
        doc_count: await this.countUserDocs(),
      });
    } catch {
      // Telemetry must never throw or affect replication startup.
    }
  }

  private async captureDeltaSyncCompleted(): Promise<void> {
    const lifecycleAtLaunch = this.lifecycleVersion;
    try {
      // Race teardown so an offline start that never reaches in-sync stops awaiting on logout.
      await Promise.race([
        Promise.all(Array.from(this.replicationStates.values()).map((state) => state.awaitInSync())),
        this.lifecycleTeardown.promise,
      ]);
      // A late resolve (after logout or a leadership handoff) must not emit against a reset baseline.
      if (this.lifecycleVersion !== lifecycleAtLaunch) return;
      capture('delta_sync_completed', {
        time_to_synced_ms: this.timeSinceEntry(),
        ...this.usableCopy(),
      });
    } catch {
      // Telemetry must never throw or affect replication startup.
    }
  }

  private timeSinceEntry(): number {
    return Math.round(performance.now() - this.appEntryAtMs!);
  }

  private usableCopy(): { time_to_usable_ms?: number } {
    if (this.usableMs === undefined) return {};
    return { time_to_usable_ms: this.usableMs };
  }

  async countUserDocs(): Promise<number> {
    if (!this.rxCollections) return 0;

    const { notes, userSources } = this.rxCollections;
    // notes/userSources are archive-partitioned, so `archived` is required + indexed: a plain
    // equality count hits the index instead of the slow full-read the two-arm selector forces.
    const counts = await Promise.all([
      notes.count({ selector: { archived: false } }).exec(),
      userSources.count({ selector: { archived: false } }).exec(),
    ]);
    return counts.reduce((total, count) => total + count, 0);
  }

  private setupNetworkDetection(): void {
    this.onlineHandler = () => {
      console.debug('[REPLICATION] Network reconnected, resyncing');
      this.replicationStates.forEach((state) => state.reSync());
      this.archivedReplicationStates.forEach((state) => state.reSync());
    };

    window.addEventListener('online', this.onlineHandler);
  }

  private async createRxDB(): Promise<RxDocumintDatabase> {
    const workers: Worker[] = [];

    try {
      const rxDB = await createRxDatabase<RxDatabaseCollections>({
        name: RXDB_NAME,
        storage: this.getStorage(workers),
        ignoreDuplicate: ENVIRONMENT.MODE === 'test',
        multiInstance: true,
      });
      this.storageWorkers.set(rxDB, workers);
      return rxDB;
    } catch (error) {
      terminateWorkers(workers);
      throw error;
    }
  }

  // The worker storage never terminates the Web Workers it opens: close() only detaches its
  // listeners. Creating them through a factory keeps a handle, so teardown can terminate them and
  // release their IndexedDB connections (see cleanupRxDBInstance).
  private getStorage(workers: Worker[]) {
    const workerInput = () => {
      const worker = new Worker(INDEXED_DB_WORKER_PATH);
      workers.push(worker);
      return worker;
    };

    if (ENVIRONMENT.MODE === 'production') {
      return getRxStorageWorker({ workerInput });
    }
    return wrappedValidateAjvStorage({
      storage: getRxStorageWorker({ workerInput }),
    });
  }

  // Archived docs are kept out of the search index: skip archived inserts, and on
  // update evict newly-archived docs while (re)indexing the rest — the latter path
  // also re-inserts restored docs, which the worker's update tolerates.
  private initSearchHooks(rxCollection: RxCollection) {
    this.wireSearchHooks(rxCollection, {
      service: searchService,
      indexes: (doc) => !isArchived(doc),
      evicts: (changeEvent) => isArchived(changeEvent.documentData),
      post: (send) => void send(),
    });
  }

  // Complement of initSearchHooks for the archived index: archived docs are indexed;
  // an update to non-archived means the doc was restored → evict; remove$ covers the
  // deletion of an archived doc → evict. Only archive-partitioned collections subscribe
  // (see startArchivedSearchIndex), and only after the first archive open. Sends route
  // through sendOrQueueArchived so they queue while the reconcile bracket is open.
  private initArchivedSearchHooks(rxCollection: RxCollection) {
    this.wireSearchHooks(rxCollection, {
      service: archivedSearchService,
      indexes: (doc) => isArchived(doc),
      evicts: (changeEvent) => !isArchived(changeEvent.documentData) && isArchived(changeEvent.previousDocumentData),
      post: (send) => this.sendOrQueueArchived(send),
    });
  }

  // Shared wiring for both index-membership polarities. `indexes` decides which docs
  // belong in this index; `evicts` decides which updated docs must leave it; `post`
  // dispatches a send (direct fire-and-forget for the main index, queued during the
  // reconcile bracket for the archived one).
  private wireSearchHooks(
    rxCollection: RxCollection,
    wiring: {
      service: SearchService;
      indexes: (doc: any) => boolean;
      evicts: (changeEvent: { documentData: any; previousDocumentData?: any }) => boolean;
      post: (send: () => Promise<void>) => void;
    }
  ) {
    const { service, indexes, evicts, post } = wiring;

    // Subscribe order (insert before remove) keeps cross-stream flush ordering
    // sane: a doc inserted then removed in the same window flushes insert first.
    rxCollection.insert$.pipe(bufferNonEmpty()).subscribe((changeEvents) => {
      const rawDocs = changeEvents
        .filter((changeEvent) => indexes(changeEvent.documentData))
        .map((changeEvent) => this.toRawSearchDoc(changeEvent));
      if (rawDocs.length) {
        post(() => service.addDocs(rawDocs));
      }
    });

    rxCollection.update$.pipe(bufferNonEmpty()).subscribe((changeEvents) => {
      // A buffer window can hold several events for the same doc (e.g. archived then
      // restored); only the latest one per id decides index membership. Events are
      // chronological, so the last write into the map wins.
      const latestEvents = Array.from(
        new Map(changeEvents.map((changeEvent) => [changeEvent.documentData.id, changeEvent])).values()
      );

      const rawDocs = latestEvents
        .filter((changeEvent) => indexes(changeEvent.documentData))
        .map((changeEvent) => this.toRawSearchDoc(changeEvent));
      if (rawDocs.length) {
        post(() => service.updateDocs(rawDocs));
      }

      const evictedIds = latestEvents
        .filter((changeEvent) => evicts(changeEvent))
        .map((changeEvent) => changeEvent.documentData.id);
      if (evictedIds.length) {
        post(() => service.removeDocs(evictedIds));
      }
    });

    rxCollection.remove$.pipe(bufferNonEmpty()).subscribe((changeEvents) => {
      post(() => service.removeDocs(changeEvents.map((changeEvent) => changeEvent.documentId)));
    });
  }

  // Post immediately outside the reconcile bracket; queue while it is open (see the
  // pendingArchivedSends comment for why interleaving is unsafe). Posting order to the
  // worker is what matters, so the flush replays synchronously — completion promises
  // are intentionally not awaited (same fire-and-forget as the direct sends).
  private sendOrQueueArchived(send: () => Promise<void>) {
    if (this.archivedReconcileInProgress) {
      this.pendingArchivedSends.push(send);
      return;
    }
    void send();
  }

  private flushPendingArchivedSends() {
    const pending = this.pendingArchivedSends;
    this.pendingArchivedSends = [];
    this.archivedReconcileInProgress = false;
    pending.forEach((send) => void send());
  }

  private toRawSearchDoc(changeEvent: { documentData: any; collectionName?: string }): RawSearchDoc {
    const collectionName = changeEvent.collectionName as RxCollectionName;
    const doc = changeEvent.documentData;
    // tags don't carry tagIds; every other collection's builder consumes tagNames.
    const tagNames = collectionName === 'tags' ? [] : tagNameCache.resolveTagNames(doc.tagIds);
    return { collectionName, doc, tagNames };
  }

  private wrapWithSyncStatus<T extends (...args: any[]) => any>(fn: T | undefined): T {
    return ((...args) => {
      void this.updateSyncStatus(true);
      return fn ? fn(...args) : args[0];
    }) as T;
  }

  private initMiddlewareHooks(rxCollection: RxCollection, rxCollections: RxDatabaseCollections) {
    const preInsert = this.wrapWithSyncStatus(getPreInsert({ rxCollection, rxCollections }));
    rxCollection.preInsert(preInsert, true);

    const preSave = this.wrapWithSyncStatus(getPreSave({ rxCollection, rxCollections }));
    rxCollection.preSave(preSave, true);

    const postSave = getPostSave({ rxCollection, rxCollections });
    if (postSave) {
      rxCollection.postSave(postSave, true);
    }

    const postRemove = this.wrapWithSyncStatus(getPostRemove({ rxCollection, rxCollections }));
    rxCollection.postRemove(postRemove, true);
  }

  // tags.numDocs is maintained from committed writes rather than from the middleware hooks (see
  // docs/tag-counts/tag-counts-architecture.md). The tag write still goes through the tags collection, so it replicates
  // as before and its own preSave hook raises the awaiting-sync flag; only the moment the write is
  // issued moved past the doc commit, which is what the tracking below covers. The subscription
  // needs no teardown: closing the database completes eventBulks$ (see cleanupRxDBInstance), like
  // the search hooks' collection subscriptions.
  private initTagCountSubscription(rxCollection: RxCollection, rxCollections: RxDatabaseCollections) {
    if (!TAGGED_COLLECTIONS.includes(rxCollection.name)) {
      return;
    }

    subscribeTagCountsToLocalWrites(rxCollection, rxCollections.tags, (write) => this.trackTagCountWrite(write));
  }

  // Deliberately does not raise the awaiting-sync flag: a counted bulk does not always produce a
  // tag write (updateTagNumDocs writes nothing when the owned tagIds are unchanged), and a
  // `replication-up-write-conflict` rollback has no accompanying document push to clear the flag
  // again — it would stay true, with nothing to send, until the next edit. The tags collection's
  // own preSave hook raises it exactly when a write does happen.
  private trackTagCountWrite(write: Promise<void>): void {
    this.pendingTagCountWrites.add(write);
    void write.finally(() => this.pendingTagCountWrites.delete(write));
  }

  // A count write can settle while another bulk is still arriving, so drain until the set empties
  // rather than snapshotting it once. Promise.all is safe: a tracked write already swallows its
  // own failure (see subscribeTagCountsToLocalWrites), so none of them can reject.
  private async awaitPendingTagCountWrites(): Promise<void> {
    while (this.pendingTagCountWrites.size) {
      await Promise.all([...this.pendingTagCountWrites]);
    }
  }

  private replicateCollection(rxCollection: RxCollection, firestore: Firestore, userId: string): void {
    const pullFilter = pullFilterFor(rxCollection, userId);

    // The normalized jsonSchema carries rxdb's meta properties (_rev, _meta, _attachments,
    // _deleted); shelling off it would write those to Firestore and diverge from the
    // shells the one-time strip script builds off the raw exported schema.
    const pushSchema = rxCollection.schema.getJsonSchemaWithoutMeta();
    const firestoreTarget = buildFirestoreReplicationTarget(rxCollection, firestore);

    const replicationOptions: SyncOptionsFirestore<any> = {
      collection: rxCollection,
      firestore: firestoreTarget,
      pull: {
        filter: pullFilter,
      },
      // The plugin's push conflict-check queries documentId()-in with no userId filter;
      // firestore.rules evaluates that shape per document, so the ownership list rule
      // (with its resource == null disjunct) admits it — verified against real dev
      // Firestore and in packages/security-rules-tests.
      // required for replication to remote
      push: {
        modifier: (doc) => (isDeleted(doc) ? buildTombstoneShell(doc, pushSchema) : doc),
      },
      live: true,
      replicationIdentifier: REPLICATION_IDENTIFIER,
    };

    const replicationState = replicateFirestore(replicationOptions);
    // See replication-push-pre-read.ts: forces a real server read of the pushed docs before the
    // plugin's own (cache-susceptible) conflict check runs. Swapping the handler here is early
    // enough: RxDB reads push.handler on every push, and the plugin starts itself asynchronously.
    replicationState.push!.handler = withServerPreRead(
      replicationState.push!.handler,
      firestoreTarget.collection,
      rxCollection.schema.primaryPath
    );
    // Before the start, so no push conflict can resolve unrecorded: a conflict rollback is counted
    // against the master its push assumed (see docs/tag-counts/tag-counts-architecture.md).
    if (TAGGED_COLLECTIONS.includes(rxCollection.name)) {
      recordPushConflictsForTagCounts(replicationState);
    }

    this.replicationStates.set(rxCollection.name, replicationState);

    // replicationState.received$.subscribe((docData) => {
    // console.debug('$received', rxCollection.name, docData);
    // });

    replicationState.sent$.subscribe(() => {
      void this.syncAndClearFlag();
    });

    this.subscribeToReplicationErrors(rxCollection.name, replicationState, 'active');
  }

  private replicateArchivedCollection(rxCollection: RxCollection, firestore: Firestore, userId: string): void {
    // Pull-only: local writes already push through the active partition, so a push here would
    // double-push. The identifier suffix keeps this partition's checkpoint separate — the
    // plugin's meta collection is keyed by collection name + replicationIdentifier.
    const replicationState = replicateFirestore({
      collection: rxCollection,
      firestore: buildFirestoreReplicationTarget(rxCollection, firestore),
      pull: {
        filter: [where('userId', '==', userId), where('archived', '==', true)],
      },
      live: true,
      replicationIdentifier: `${REPLICATION_IDENTIFIER}-archived`,
    });

    this.archivedReplicationStates.set(rxCollection.name, replicationState);

    // The active upstream has no record of what this partition pulls, so the unpushed-write count
    // must be told (see countUnpushedWrites). A count may have created the list already.
    const pulled = this.ensureArchivePulledFingerprints(rxCollection.name);
    // Hashing is asynchronous, so two states of one document can resolve out of order: only the
    // fingerprint of the latest one received is recorded.
    const latestReceived = new Map<string, unknown>();
    replicationState.received$.subscribe((doc) => {
      latestReceived.set(doc.id, doc);
      void fingerprintServerState(doc).then((fingerprint) => {
        if (latestReceived.get(doc.id) !== doc) {
          return;
        }
        latestReceived.delete(doc.id);
        pulled.fingerprints.set(doc.id, fingerprint);
        this.scheduleArchivePulledFingerprintsSave(rxCollection.name);
      });
    });

    this.subscribeToReplicationErrors(rxCollection.name, replicationState, 'archived');
  }

  private ensureArchivePulledFingerprints(collectionName: string): ArchivePulledFingerprints {
    let pulled = this.archivePulledFingerprints.get(collectionName);
    if (!pulled) {
      pulled = { fingerprints: new Map(), pendingSave: undefined, savesStopped: false, storedMerged: false };
      this.archivePulledFingerprints.set(collectionName, pulled);
    }
    return pulled;
  }

  // Looks the list up by name, as the save does: after logout's clearRxDB there is none to save.
  private scheduleArchivePulledFingerprintsSave(collectionName: string): void {
    const pulled = this.archivePulledFingerprints.get(collectionName);
    if (!pulled || pulled.pendingSave || pulled.savesStopped) {
      return;
    }
    pulled.pendingSave = setTimeout(() => this.saveArchivePulledFingerprints(collectionName), FINGERPRINT_SAVE_DELAY_MS);
  }

  // Saves every pending list now, for a page that is going away, in place of its timer. A document
  // whose fingerprint is still being computed is not in it yet; it schedules a save of its own.
  private saveScheduledArchivePulledFingerprints(): void {
    this.archivePulledFingerprints.forEach((pulled, collectionName) => {
      if (pulled.pendingSave) {
        this.saveArchivePulledFingerprints(collectionName);
      }
    });
  }

  // The stored list is merged into memory at the first save, so from then on memory alone is saved.
  private saveArchivePulledFingerprints(collectionName: string): void {
    // Gone after logout's clearRxDB: nothing may be written back after its localStorage.clear().
    const pulled = this.archivePulledFingerprints.get(collectionName);
    if (!pulled) {
      return;
    }
    clearTimeout(pulled.pendingSave);
    pulled.pendingSave = undefined;

    mergeStoredFingerprints(pulled, collectionName);
    const serialized = JSON.stringify(Object.fromEntries(pulled.fingerprints));
    if (serialized.length > MAX_STORED_FINGERPRINTS_LENGTH) {
      stopSavingArchivePulledFingerprints(pulled, collectionName);
      return;
    }
    try {
      localStorage.setItem(ARCHIVE_PULLED_FINGERPRINTS_KEY_PREFIX + collectionName, serialized);
    } catch {
      // Over quota.
      stopSavingArchivePulledFingerprints(pulled, collectionName);
    }
  }

  private subscribeToReplicationErrors(
    collectionName: string,
    replicationState: RxFirestoreReplicationState<any>,
    partition: 'active' | 'archived'
  ): void {
    replicationState.error$.subscribe((error) => {
      const signature = replicationErrorSignature(error);

      if (signature === 'permission-denied' && !getAuth().currentUser) {
        // waiting for firebase auth
        console.debug(`[REPLICATION] Auth pending for ${collectionName}, retrying...`);
        return;
      }

      if (signature === 'unavailable' && !navigator.onLine) {
        // Expected while offline: pull, push and the push pre-read (see replication-push-pre-read.ts)
        // read from the server and reject until it is reachable again, and RxDB retries. Nothing
        // could reach Sentry in this state either. An unavailable while navigator.onLine is true
        // reports, on every retry.
        return;
      }

      // Signed in yet denied (permission-denied): roles were revoked or the rules are broken, so surface it.
      console.error(`[REPLICATION] Error in ${collectionName} (${partition}) [${signature}]:`, error);
    });
  }

  /**
   * How many local writes have not reached Firestore yet — what logout must never delete unasked.
   * Read from each pushing replication's persisted upstream checkpoint (see unpushed-writes.ts), not
   * from the awaiting-sync flag: initSyncState resets the flag on every app load, while RxDB resumes
   * the queued push after a reload.
   *
   * Only the RxDB leader tab runs replication (multiInstance + waitForLeadership), so only it can
   * read those checkpoints. Any other tab asks the leader over the tab-sync channel; the storage is
   * shared, so the leader's count includes this tab's writes.
   *
   * Resolves undefined when it cannot be told: a local database is on disk but this tab could not
   * open it (e.g. the DB6 recovery), a collection whose replication has not started stores
   * documents, or no leader answered. No database at all is 0. A database still opening is counted
   * once open; logout bounds the wait.
   */
  async getUnpushedWriteCount(): Promise<number | undefined> {
    if (!this.rxDB && this.rxDBInitPromise) {
      // A failed open leaves rxDB unset; its caller reports the failure.
      await this.rxDBInitPromise.catch(() => undefined);
    }

    if (!this.rxDB) {
      return (await hasLocalDatabaseOnDisk()) ? undefined : 0;
    }

    if (this.rxDB.isLeader()) {
      return this.countUnpushedWritesInThisTab();
    }

    // This tab's own tag-count writes are issued here, so they must be committed before the leader
    // counts; loop in case another one is registered while the answer is pending.
    let count: number | undefined;
    do {
      await this.awaitPendingTagCountWrites();
      count = await tabSyncService.requestUnpushedWriteCount(LEADER_COUNT_TIMEOUT_MS);
    } while (this.pendingTagCountWrites.size);

    return count;
  }

  // Leader only: replication states elsewhere never start, so they have nothing to read.
  private async countUnpushedWritesInThisTab(): Promise<number | undefined> {
    // Drain before counting: a tag-count write issued after its document commit is not in storage
    // yet at the instant the stream hands it over. Then re-check, because another bulk can be counted
    // while the count is being read — so loop until the pending set is empty after a full pass. No
    // artificial bound: the loop ends when writes stop arriving, and logout bounds its callers.
    let counts: Array<number | undefined>;
    do {
      await this.awaitPendingTagCountWrites();
      // Every collection startReplication replicates, so one whose replication has not started still
      // counts. None once logout's clearRxDB dropped them, which leaves the count undefined.
      counts = await Promise.all(
        Object.values(this.rxCollections ?? {}).map((rxCollection) => {
          const state = this.replicationStates.get(rxCollection.name);
          return state?.internalReplicationState
            ? countUnpushedWrites(state, this.archivePulledFingerprintsFor(rxCollection.name))
            : countUnpushedWritesWithoutReplication(rxCollection);
        })
      );
    } while (this.pendingTagCountWrites.size);

    if (!counts.length || counts.includes(undefined)) {
      return undefined;
    }
    return (counts as number[]).reduce((total, count) => total + count, 0);
  }

  // The live list, not a copy: a fingerprint recorded while the count awaits its reads must still be
  // seen. Undefined once logout's clearRxDB dropped the collections, so nothing is re-created to save.
  private archivePulledFingerprintsFor(collectionName: string): Map<string, string> | undefined {
    const rxCollection = this.rxCollections?.[collectionName as RxCollectionName];
    if (!rxCollection || !isArchivePartitioned(rxCollection)) {
      return undefined;
    }

    const pulled = this.ensureArchivePulledFingerprints(collectionName);
    mergeStoredFingerprints(pulled, collectionName);
    return pulled.fingerprints;
  }

  // Resolves once every local write has been pushed, or as soon as `signal` aborts — each pass asks
  // the leader tab for a count, so an abandoned wait must stop polling. Unbounded otherwise: the only
  // caller (use-safe-logout) races it against a timeout and a cancellable modal.
  async awaitInSync(signal?: AbortSignal): Promise<void> {
    while (!signal?.aborted && (await this.getUnpushedWriteCount()) !== 0) {
      await new Promise((resolve) => setTimeout(resolve, UNPUSHED_WRITES_RECHECK_MS));
    }
  }

  // Logout calls this after localStorage.clear() when clearRxDB could not delete the databases.
  scheduleDatabaseDeletionOnNextStart(): void {
    localStorage.setItem(PENDING_DATABASE_DELETION_KEY, 'true');
  }

  initialSyncComplete$(): Observable<boolean> {
    return getInitialSyncComplete$(this.syncState!);
  }

  knownEmptyAccount$(): Observable<boolean> {
    return this.emptyAccount$.asObservable();
  }

  async hasLocalData(): Promise<boolean> {
    if (!this.rxCollections) return false;

    const collections = Object.values(this.rxCollections);
    const hasDataChecks = collections.map(async (c) => {
      // Archive-partitioned collections count only active docs via the fast indexed equality;
      // the rest have no `archived` field, so a plain count is both correct and index-free.
      const query = isArchivePartitioned(c) ? c.count({ selector: { archived: false } }) : c.count();
      const count = await query.exec();
      if (count > 0) return true;
      throw new Error('empty');
    });

    try {
      return await Promise.any(hasDataChecks);
    } catch {
      return false; // AggregateError - all collections empty
    }
  }

  async getUserDocument(userId: string): Promise<RxDocument<User> | null> {
    const users = this.rxCollections?.users;
    if (!users) {
      return null;
    }

    return (await users.findOne(userId).exec()) ?? null;
  }

  async rebuildSearchIndex(): Promise<void> {
    if (!this.rxCollections) {
      throw new Error('rxCollections must be initialized before rebuilding search index');
    }

    // Join an in-flight rebuild: the stale-path prime and the restore-failed heal both fire this
    // independently, and overlapping brackets would let the first rebuildFinished unlatch persists
    // while the second still feeds docs (partial persist). One shared promise = one bracket.
    if (this.rebuildInProgress) {
      return this.rebuildInProgress;
    }

    const rebuild = this.runRebuildSearchIndex();
    this.rebuildInProgress = rebuild;

    try {
      await rebuild;
    } finally {
      if (this.rebuildInProgress === rebuild) {
        this.rebuildInProgress = undefined;
      }
    }
  }

  private async runRebuildSearchIndex(): Promise<void> {
    searchService.setIndexStatus('rebuilding');

    try {
      // Suppress batch persists in the worker for the whole rebuild so no partial
      // index reaches the active index key (I1). rebuildFinished (below) re-enables them and
      // persists the complete index once.
      await searchService.rebuildStarted();

      await this.streamCollectionsToIndex(
        Object.values(this.rxCollections!).filter((rxCollection) => isSearchIndexed(rxCollection)),
        notArchivedSelector,
        (rawDocs) => searchService.rebuildDocs(rawDocs)
      );

      // Success only: persist the now-complete index and re-enable batch persists.
      // On failure we deliberately do NOT reach here: the catch sends rebuildAborted instead,
      // which leaves the worker's persist suppression latched true for the session — a
      // one-way latch so nothing partial ever persists; the next session retries.
      await searchService.rebuildFinished();
    } catch (error) {
      // Failure only: clear the worker's removed-id tracking without persisting.
      // Best-effort and not awaited, so it can never mask or delay the original rejection.
      searchService.rebuildAborted().catch(() => {});
      throw error;
    } finally {
      // Always open the search gate, even if the rebuild rejected (e.g. a worker
      // message timeout): a stuck 'rebuilding' would show loading rows for the whole
      // session. A failed rebuild leaves the index partial for the rest of the session and
      // nothing partial is persisted (see the one-way latch above) while rebuildStarted already
      // deleted the stored blob, so a reload finds no index and cold-rebuilds;
      // serving partial results beats a permanent loading state.
      // Only when still 'rebuilding': a concurrent terminate() (logout/worker-error) resets
      // the status to 'initializing', and clobbering that with 'ready' would re-seed a torn-down
      // session's status against an empty index.
      if (searchService.indexStatus.value === 'rebuilding') {
        searchService.setIndexStatus('ready');
      }
    }
  }

  // Shared feed driver for the full-index paths (rebuild and archived reconcile):
  // query each collection with `selector`, chunk into REBUILD_INDEX_BATCH_SIZE
  // batches, and await `send` per batch for backpressure and main-thread yielding
  // between chunks. RawSearchDoc construction goes through toRawSearchDoc; its tags
  // special-case can only ever fire on the rebuild path — the reconcile streams
  // archive-partitioned collections, which never include tags.
  private async streamCollectionsToIndex(
    rxCollections: RxCollection[],
    selector: MangoQuerySelector<any>,
    send: (rawDocs: RawSearchDoc[]) => Promise<void>
  ): Promise<void> {
    for (const rxCollection of rxCollections) {
      const collectionName = rxCollection.name as RxCollectionName;
      const docs = await rxCollection.find({ selector }).exec();

      for (let i = 0; i < docs.length; i += REBUILD_INDEX_BATCH_SIZE) {
        const chunk = docs.slice(i, i + REBUILD_INDEX_BATCH_SIZE);
        const rawDocs = chunk.map((doc) =>
          this.toRawSearchDoc({
            collectionName,
            documentData: typeof doc.toJSON === 'function' ? doc.toJSON() : (doc as any),
          })
        );
        await send(rawDocs);
      }
    }
  }

  // Startup sweep for the persisted-index path: an archive within the search-hook buffer
  // window before a close/reload never reaches the worker, while the earlier-persisted
  // index still holds the doc — and the restore replays it with no reconciliation.
  // Evicting every currently-archived doc's id repairs that; the worker's remove
  // tolerates ids absent from the index.
  async evictArchivedDocsFromSearchIndex(): Promise<void> {
    if (!this.rxCollections) {
      throw new Error('rxCollections must be initialized before evicting archived docs from search index');
    }

    await Promise.all(
      this.archivePartitionedCollections.map(async (rxCollection) => {
        const docs = await rxCollection.find({ selector: { archived: true } }).exec();
        const archivedIds = docs.map((doc) => doc.id);
        if (archivedIds.length) {
          await searchService.removeDocs(archivedIds);
        }
      })
    );
  }

  // Lazy entry point for the archived search index (DM-1460), called on the first
  // useArchivedSearch mount. Order matters: spawn the worker (restore begins),
  // subscribe live hooks BEFORE the snapshot query (no event gap), then reconcile the
  // restored index against RxDB truth. A restore-failed or missing blob needs no
  // special casing — the reconcile degrades to a plain initial build.
  async ensureArchivedSearchIndex(): Promise<void> {
    if (!this.rxCollections) {
      throw new Error('rxCollections must be initialized before starting archived search');
    }

    if (this.archivedSearchInitPromise) {
      return this.archivedSearchInitPromise;
    }

    const init = this.startArchivedSearchIndex();
    this.archivedSearchInitPromise = init;

    try {
      await init;
    } catch (error) {
      if (this.archivedSearchInitPromise === init) {
        this.archivedSearchInitPromise = undefined;
      }
      throw error;
    }
  }

  private async startArchivedSearchIndex(): Promise<void> {
    archivedSearchService.ensureStarted();

    const archivedCollections = this.archivePartitionedCollections;

    if (!this.archivedSearchHooksInitialized) {
      archivedCollections.forEach((rxCollection) => this.initArchivedSearchHooks(rxCollection));
      this.archivedSearchHooksInitialized = true;
    }

    await this.reconcileArchivedSearchIndex(archivedCollections);
  }

  private async reconcileArchivedSearchIndex(archivedCollections: RxCollection[]): Promise<void> {
    // Open the queue gate synchronously before the first await: the hooks subscribed
    // just above (same synchronous sequence, so no event can slip between) and every
    // live event from here to the flush must queue, not post (see pendingArchivedSends).
    this.archivedReconcileInProgress = true;

    // 'rebuilding' is set synchronously, before the worker's restore announce can
    // arrive, so useArchivedSearch stays loading and the update listener stays
    // unsubscribed until the reconcile completes; handleWorkerMessage deliberately
    // swallows the announce while 'rebuilding'.
    archivedSearchService.setIndexStatus('rebuilding');

    try {
      await archivedSearchService.reconcileStarted();

      await this.streamCollectionsToIndex(archivedCollections, { archived: true }, (rawDocs) =>
        archivedSearchService.reconcileDocs(rawDocs)
      );

      await archivedSearchService.reconcileFinished();
    } finally {
      // Drain queued live events first (they are strictly newer truth than the
      // snapshot — correct to apply even after a failed reconcile) so results are
      // current the moment the gate opens.
      this.flushPendingArchivedSends();

      // Always open the search gate (mirrors runRebuildSearchIndex): on failure the
      // in-memory index is complete or near-complete and nothing partial persisted
      // (the worker's reconcile latch stays closed). Only when still 'rebuilding': a
      // concurrent terminate() resets to 'initializing' and must not be clobbered.
      if (archivedSearchService.indexStatus.value === 'rebuilding') {
        archivedSearchService.setIndexStatus('ready');
      }
    }
  }

  /**
   * Tears the local data down for logout. Resolves true only when this call deleted the local
   * databases; false leaves that to the leader tab or to the next start (the caller schedules it,
   * see scheduleDatabaseDeletionOnNextStart). Every step is bounded so logout always completes.
   *
   * `deleteDatabases: false` is for a tab following another tab's logout: it still closes its
   * connections, which would otherwise keep that tab's deletion blocked, but deletes nothing.
   */
  async clearRxDB({ deleteDatabases = true }: { deleteDatabases?: boolean } = {}): Promise<boolean> {
    const replicationStates = [...this.replicationStates.values(), ...this.archivedReplicationStates.values()];

    this.lifecycleVersion += 1;
    this.lifecycleTeardown.resolve();
    this.lifecycleTeardown = deferred<void>();
    this.rxDBInitPromise = undefined;
    this.replicationInitPromise = undefined;
    this.rebuildInProgress = undefined;
    this.replicationStates.clear();
    this.archivedReplicationInitPromise = undefined;
    this.archivedReplicationStates.clear();
    this.archivePulledFingerprints.clear();
    this.archivedSearchInitPromise = undefined;
    this.archivedSearchHooksInitialized = false;
    this.archivedReconcileInProgress = false;
    this.pendingArchivedSends = [];
    this.emptyAccount$.next(false);
    this.pendingTagCountWrites.clear();

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }

    tagNameCache.reset();
    // Storage download URLs are scoped to the signed-out user's prefix; they must not survive into
    // the next session in this tab.
    clearPreviewUrlCache();
    searchService.terminate();
    archivedSearchService.terminate();

    const rxDB = this.rxDB;
    this.rxDB = undefined;
    this.rxCollections = undefined;
    this.syncState = undefined;
    this.syncStatusWriteQueue = Promise.resolve();
    this.appEntryAtMs = undefined;
    this.usableMs = undefined;

    // Before the close: a live pull keeps writing into the storage queue that close() waits to
    // drain, and only close() itself would otherwise cancel it.
    await runTeardownStep('Cancelling replications', () => Promise.all(replicationStates.map((state) => state.cancel())));

    // Leadership must be settled before the close: closing the database kills this tab's
    // leader elector, so isLeader/waitForLeadership stop answering afterwards.
    const ownsDeletion = deleteDatabases && (await this.claimDatabaseDeletion(rxDB));

    // Unconditional in every tab that logs out: releases this tab's connections, which would
    // otherwise keep the leader's delete blocked.
    await this.cleanupRxDBInstance(rxDB, { remove: false });

    if (!ownsDeletion) {
      if (deleteDatabases) {
        console.warn('[CLEANUP] Another tab holds RxDB leadership, leaving the IndexedDB deletion to it');
      }
      return false;
    }

    return this.deleteLocalDatabases();
  }

  /**
   * A forced sign-out (revoked refresh tokens, disabled account) runs the logout flow in every
   * open tab at once, and concurrent indexedDB.deleteDatabase calls just block on each other's
   * connections. Only the leader tab runs them. A follower waits briefly to inherit leadership -
   * the prior leader may have died, in which case nobody else is left to delete - and otherwise
   * defers. Scoped to the raw database deletion: the close runs in every tab regardless.
   */
  private async claimDatabaseDeletion(rxDB: RxDocumintDatabase | undefined): Promise<boolean> {
    if (!rxDB) {
      return true;
    }

    try {
      if (rxDB.isLeader()) {
        return true;
      }

      return await Promise.race([
        rxDB.waitForLeadership(),
        new Promise<boolean>((resolve) => setTimeout(resolve, DELETION_LEADERSHIP_TIMEOUT_MS, false)),
      ]);
    } catch (error) {
      console.warn('[CLEANUP] Leader election unavailable, deleting the local database anyway:', error);
      return true;
    }
  }

  // Logout never removes through RxDB: remove() opens a fresh storage instance, which spawns a new
  // worker holding a new connection to the database the raw delete then waits on. Only an init that
  // a logout cancelled removes, since that logout's delete may already have run.
  private async cleanupRxDBInstance(rxDB: RxDocumintDatabase | undefined, { remove }: { remove: boolean }): Promise<void> {
    if (!rxDB) {
      return;
    }

    await runTeardownStep('Closing the RxDB database', () => rxDB.close());

    if (remove) {
      await runTeardownStep('Removing the RxDB database', () => rxDB.remove());
    }

    terminateWorkers(this.storageWorkers.get(rxDB) ?? []);
    this.storageWorkers.delete(rxDB);
  }

  // The database must not be opened while this deletion is pending: it would show the previous
  // account's data, and the deletion retried on a later start would erase this session's writes.
  // The usual blocker is another tab still holding the database, which lets go within moments, so
  // retry for a while before giving up (the marker stays for the next start).
  private async deleteDatabasesLeftByLogout(): Promise<void> {
    if (!localStorage.getItem(PENDING_DATABASE_DELETION_KEY)) {
      return;
    }

    for (let attempt = 1; attempt <= PENDING_DELETION_ATTEMPTS; attempt++) {
      if (await this.deleteLocalDatabases()) {
        localStorage.removeItem(PENDING_DATABASE_DELETION_KEY);
        return;
      }
      if (attempt < PENDING_DELETION_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, PENDING_DELETION_RETRY_MS));
      }
    }

    throw new LocalDataLeftByLogoutError();
  }

  private async deleteLocalDatabases(): Promise<boolean> {
    const results = await Promise.all(
      [RXDB_NAME, SEARCH_INDEX_DB_NAME].map((databaseName) => this.deleteIndexedDBDatabase(databaseName))
    );
    return results.every(Boolean);
  }

  // Resolves whether the database was deleted. A failure is reported through console.error (the
  // Sentry path) rather than warned past: a database left on disk opens for the next login.
  private deleteIndexedDBDatabase(databaseName: string): Promise<boolean> {
    return new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(databaseName);
      let blockedTimeout: ReturnType<typeof setTimeout> | undefined;
      let settled = false;

      const finish = (deleted: boolean) => {
        if (settled) {
          return;
        }

        settled = true;
        if (blockedTimeout) {
          clearTimeout(blockedTimeout);
        }
        resolve(deleted);
      };

      request.onsuccess = () => finish(true);

      request.onerror = () => {
        console.error(`[CLEANUP] Failed to delete IndexedDB database ${databaseName}`, request.error);
        finish(false);
      };

      request.onblocked = () => {
        if (blockedTimeout) {
          return;
        }

        console.warn(
          `[CLEANUP] IndexedDB delete blocked for ${databaseName}, waiting up to ${INDEXED_DB_DELETE_BLOCKED_TIMEOUT_MS}ms for other connections to close`
        );

        blockedTimeout = setTimeout(() => {
          console.error(`[CLEANUP] Timed out deleting IndexedDB database ${databaseName}, other connections stayed open`);
          finish(false);
        }, INDEXED_DB_DELETE_BLOCKED_TIMEOUT_MS);
      };
    });
  }

  private throwIfLifecycleChanged(initLifecycleVersion: number): void {
    if (!this.isLifecycleCurrent(initLifecycleVersion)) {
      throw new LocalDataInitCancelledError();
    }
  }

  private isLifecycleCurrent(initLifecycleVersion: number): boolean {
    return initLifecycleVersion === this.lifecycleVersion;
  }

  private async initSyncState(rxDB: RxDocumintDatabase): Promise<void> {
    this.syncState = await rxDB.addState('sync');
    // Always reset awaitingSync to false on app initialization, so a stale true from a previous
    // session doesn't linger in the sync-status display. It is NOT a record of what is unpushed:
    // RxDB resumes a queued push after a reload regardless, which is why logout gates on
    // getUnpushedWriteCount instead of this flag.
    // Only awaitingSync — the initialSyncComplete latch must never be reset on init
    // (see sync-status.ts; it only clears when clearRxDB deletes the database on logout).
    await this.updateSyncStatus(false);

    void this.compactSyncStateLog(rxDB);
  }

  private async compactSyncStateLog(rxDB: RxDocumintDatabase): Promise<void> {
    // The RxState op log is replayed in full (including tombstones) on every addState(),
    // so compact it once per session. Scoped strictly to the sync state's internal
    // collection — user-data tombstones follow their own contract: shelled on push,
    // cleaned locally by the RxDB cleanup plugin, and purged from Firestore server-side
    // after PURGE_AFTER_DAYS.
    // _cleanup rewrites the current state before pruning old ops, so the persisted
    // initialSyncComplete latch survives compaction.
    // Leader-gated: only the leader tab consolidates the op log, so a freshly-booted
    // follower with a stale _state can't clobber another tab's concurrent write.
    // waitForLeadership() resolves only once this tab is leader; followers stay pending
    // until they inherit leadership, which is correct — some tab always compacts eventually.
    try {
      await rxDB.waitForLeadership();
      const syncState = this.syncState;
      if (!syncState) {
        return;
      }
      await syncState._cleanup();
      await cleanupRxCollection(syncState.collection, {
        ...DEFAULT_CLEANUP_POLICY,
        minimumDeletedTime: 0,
        minimumCollectionAge: 0,
        awaitReplicationsInSync: false, // this internal collection is never replicated
        waitForLeadership: false,
      });
    } catch (error) {
      console.warn('[SYNC_STATE] Failed to compact sync state log:', error);
    }
  }

  private async syncAndClearFlag(): Promise<void> {
    if (this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;
    try {
      // Loop to a stable state instead of draining once: a `replication-up-write-conflict`
      // rollback counted DURING the replication wait owes a compensating tag write that has not
      // been issued when the states report in sync (RxDB's awaitInSync is a bounded two-pass
      // heuristic), so clearing the flag here would leave that write unsent. Unbounded by design,
      // for the same reasons as awaitInSync above.
      do {
        await this.awaitPendingTagCountWrites();
        await Promise.all(Array.from(this.replicationStates.values()).map((state) => state.awaitInSync()));
      } while (this.pendingTagCountWrites.size);
      await this.updateSyncStatus(false);
    } finally {
      this.syncInProgress = false;
    }
  }

  private updateSyncStatus(awaitingSync: boolean): Promise<void> {
    // serialize writes so the dedup check always reads settled state; a check against
    // an in-flight write would skip transitions (e.g. an edit landing while the flag clears)
    const nextWrite = this.syncStatusWriteQueue.then(async () => {
      const syncState = this.syncState;
      // Logout may have torn the database down since this update was queued (a push completing
      // mid-teardown); there is nothing left to record the flag in.
      if (!syncState || syncState.get(SYNC_STATE_KEY) === awaitingSync) {
        return;
      }
      await syncState.set(SYNC_STATE_KEY, () => awaitingSync);
    });
    this.syncStatusWriteQueue = nextWrite.catch(() => {});
    return nextWrite;
  }
}

// The user's own doc carries no searchable text and nothing consumes 'users' search
// results, so it is kept out of the index by both the live hooks and the rebuild feed.
const isSearchIndexed = (rxCollection: RxCollection) => rxCollection.name !== 'users';

// Firestore pull filter shared by pull replication and the empty-account probe: the
// probe queries what initial sync will pull, minus tombstones (see probeEmptyAccount).
const pullFilterFor = (rxCollection: RxCollection, userId: string) =>
  isArchivePartitioned(rxCollection)
    ? [where('userId', '==', userId), where('archived', '==', false)]
    : [where('userId', '==', userId)];

// Buffer per-doc change events into non-empty arrays so the search workers receive one
// message per batch (see SEARCH_HOOK_BUFFER_MS). Shared by initSearchHooks and
// initArchivedSearchHooks.
const bufferNonEmpty =
  <T>() =>
  (source: Observable<T>) =>
    source.pipe(
      bufferTime(SEARCH_HOOK_BUFFER_MS),
      filter((batch) => batch.length > 0)
    );

function buildFirestoreReplicationTarget(rxCollection: RxCollection, firestore: Firestore) {
  return {
    projectId: ENVIRONMENT.VITE_FIREBASE_PROJECT_ID as string,
    database: firestore,
    collection: collection(firestore, rxCollection.name),
  };
}

// Merges what earlier leaders stored into memory once, at this tab's first save or count — both only
// happen in the leader, whose predecessor is gone, so nothing else writes the stored list afterwards.
// Not at start-up: the tab that inherits leadership started before the previous leader pulled the
// archive. This tab's own entries win.
const mergeStoredFingerprints = (pulled: ArchivePulledFingerprints, collectionName: string): void => {
  if (pulled.storedMerged) {
    return;
  }
  pulled.storedMerged = true;
  Object.entries(readStoredFingerprints(collectionName)).forEach(([id, fingerprint]) => {
    if (!pulled.fingerprints.has(id)) {
      pulled.fingerprints.set(id, fingerprint);
    }
  });
};

// A missing or unreadable stored value, or anything but a plain object, is an empty list.
const readStoredFingerprints = (collectionName: string): Record<string, string> => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(ARCHIVE_PULLED_FINGERPRINTS_KEY_PREFIX + collectionName) ?? '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? (stored as Record<string, string>) : {};
  } catch {
    return {};
  }
};

// Memory only from here on, and the stored list goes too: left behind, it would miss this tab's
// later pulls, so the next leader could skip a document on content older than what was last pulled.
// Without it the next leader counts those documents until its upstream records them — a false wait.
const stopSavingArchivePulledFingerprints = (pulled: ArchivePulledFingerprints, collectionName: string): void => {
  pulled.savesStopped = true;
  try {
    localStorage.removeItem(ARCHIVE_PULLED_FINGERPRINTS_KEY_PREFIX + collectionName);
  } catch {
    // Storage unavailable: there is nothing left for the next leader to read either.
  }
};

// The real cause of a replication failure is a Firestore error nested under the RxError's
// `parameters` (a push batch commits atomically, so it carries a single error), and Sentry's
// beforeSend scrub drops `parameters` and truncates the message — so it has to be lifted into the
// log message itself. rxdb's own protocol errors carry no Firestore code, hence the fallbacks:
// a local write failure signs by its status, then the outer rxdb code, then the error name.
const replicationErrorSignature = (error: {
  name: string;
  code?: string;
  parameters?: { errors?: { code?: string }[]; error?: { code?: string }; writeError?: { status?: number } };
}): string => {
  const parameters = error.parameters;
  const firestoreCode = parameters?.errors?.[0]?.code ?? parameters?.error?.code;
  if (firestoreCode) {
    return firestoreCode;
  }
  if (parameters?.writeError) {
    return `write-${parameters.writeError.status}`;
  }
  return error.code ?? error.name;
};

// A teardown step that throws or never settles degrades to a warning so logout moves on.
const runTeardownStep = async (label: string, step: () => Promise<unknown>): Promise<void> => {
  try {
    await withTimeout(step(), TEARDOWN_STEP_TIMEOUT_MS, label);
  } catch (error) {
    console.warn(`[CLEANUP] ${label} failed, continuing logout:`, error);
  }
};

// Any request still in flight on a terminated worker never answers; callers bound their awaits.
const terminateWorkers = (workers: Worker[]) => {
  workers.forEach((worker) => worker.terminate());
  workers.length = 0;
};

// Without indexedDB.databases() the answer is unknown, so assume the database is there.
const hasLocalDatabaseOnDisk = async (): Promise<boolean> => {
  try {
    const databases = await indexedDB.databases();
    return databases.some((database) => database.name === RXDB_NAME);
  } catch {
    return true;
  }
};

export const localDataService = new LocalDataService();
